import { createBrowserRouter, Navigate, Outlet } from "react-router-dom";

import ForgotPassword from "./Components/Auth/ForgotPassword";
import ResetPassword from "./Components/Auth/ResetPassword";
import SetPassword from "./Components/Auth/SetPassword";
import SignIn from "./Components/Auth/SignIn";
import AppLayout from "./Components/Layout/AppLayout";
import {
  UnAuthenticatedRoute,
  AuthenticatedRoute,
  PlatformAuthenticatedRoute,
} from "./utils/Route";

import Studies from "./Components/Studies/Studies";
// import Survey from "./Components/Survey/Survey";
import ChangePassword from "./Components/Auth/ChangePassword";
import Error404 from "./Components/Layout/404";
import CustomSignIn from "./Components/Auth/CustomSignIn";
// import WelcomePage from "./Components/Survey/WelcomePage";
// import ThankYouPage from "./Components/Survey/ThankYouPage";
// import SurveyWrapper from "./Components/Survey/SurveyWrapper";
// import ThankYouWrapper from "./Components/Survey/ThankYouWrapper";
import QuestionPrintPhase from "./Components/QuestionBuilder/QuestionPrint/QuestionPrintPhase";
import QuestionPrintForm from "./Components/QuestionBuilder/QuestionPrint/QuestionPrintForm";
import Organisations from "./Components/Organisations/Organisations";
import CreateOrganisation from "./Components/Organisations/CreateOrganisation";
import ViewOrganisation from "./Components/Organisations/ViewOrganisation/ViewOrganisation";
import Features from "./Components/Features/Features";
import OrganisationSettings from "./Components/OrganisationAdmin/Settings/Settings";
import OrganisationUsers from "./Components/OrganisationAdmin/Users/Users";
import OrganisationUserDetails from "./Components/OrganisationAdmin/Users/OrganisationUserDetails";
import OTPVerification from "./Components/Auth/OTPVerification";
import UserRegistration from "./Components/Auth/UserRegistration";
import PlatformUsers from "./Components/Platform/Users/Users";
import { platformAdminPermissions } from "./utils/roles";
import PrintResponse from "./Components/Survey/PrintResponse/PrintResponse";
import Instruments from "./Components/Platform/Instruments/Instruments";
import EmailTemplates from "./Components/Platform/EmailTemplates/EmailTemplates";
import AllStudies from "./Components/Studies/AllStudies";
import PlatformConfigTabs from "./Components/PlatformFeatures/PlatformConfigTabs";
import { ParticipantJoinCode } from "./Routes/ParticipantsRoutes";
import Home from "./Home";
// Legacy participant web redirect (replaced by /mobile/* deep links)
// import LinkParticipant from "./LinkParticipant";
import LegacyParticipantLinkRedirect from "./Components/Mobile/LegacyParticipantLinkRedirect";
import MobileAppLanding from "./Components/Mobile/MobileAppLanding";
import WebviewWrapper from "./Components/CMS/Webview/WebviewWrapper";
import LandingPage from "./Components/LandingPage/LandingPage";
import PrivacyPolicy from "./Components/LandingPage/PrivacyPolicy";
import CheckEmail from "./Components/Screening/CheckEmail";
import VerifyEmail from "./Components/Auth/VerifyEmail";
import CheckFlags from "./Components/Screening/CheckFlags";
import ThankYou from "./Components/Screening/ThankYou";

const routes = createBrowserRouter([
  {
    path: "/auth/verify",
    element: <VerifyEmail />,
  },
  {
    path: "/auth",
    element: (
      <UnAuthenticatedRoute>
        <Outlet />
      </UnAuthenticatedRoute>
    ),
    children: [
      {
        path: "login",
        element: <SignIn />,
      },
      {
        path: "login/:slug",
        element: <CustomSignIn />,
      },
      {
        path: "register/:slug",
        element: <UserRegistration />,
      },
      {
        path: "forgot-password",
        element: <ForgotPassword />,
      },
      {
        path: "reset-password/:token",
        element: <ResetPassword />,
      },
      {
        path: "signup",
        element: <SetPassword />,
      },
      {
        path: "2fa",
        element: <OTPVerification />,
      },
      {
        path: "participant/login",
        element: <ParticipantJoinCode />,
      },
    ],
  },
  {
    path: "/profile/change-password",
    element: <ChangePassword />,
  },
  {
    path: "/home",
    element: <Home />,
  },
  {
    path: "/app",
    element: (
      <AuthenticatedRoute>
        <AppLayout />
      </AuthenticatedRoute>
    ),
    children: [
      {
        path: "studies",
        element: <Studies />,
      },
      {
        path: "organisation",
        children: [
          {
            index: true,
            element: <Organisations />,
          },
          {
            path: "builder/:id",
            element: <CreateOrganisation />,
          },
          {
            path: "view/:id",
            element: <ViewOrganisation />,
          },
        ],
      },
      {
        path: "features",
        element: (
          <PlatformAuthenticatedRoute
            action={platformAdminPermissions.viewPlatform}
          >
            <Features />
          </PlatformAuthenticatedRoute>
        ),
      },
      {
        path: "settings/:id",
        element: <OrganisationSettings />,
      },
      {
        path: "users",
        children: [
          {
            index: true,
            element: <OrganisationUsers />,
          },
          {
            path: "details/:id",
            element: <OrganisationUserDetails />,
          },
        ],
      },
      {
        path: "platform-users",
        element: (
          <PlatformAuthenticatedRoute
            action={platformAdminPermissions.viewPlatform}
          >
            <PlatformUsers />
          </PlatformAuthenticatedRoute>
        ),
      },
      {
        path: "all-studies",
        children: [
          {
            index: true,
            element: (
              <PlatformAuthenticatedRoute
                action={platformAdminPermissions.managePlatform}
              >
                <AllStudies />
              </PlatformAuthenticatedRoute>
            ),
          },
          {
            path: "study/:id",
            element: (
              <PlatformAuthenticatedRoute
                action={platformAdminPermissions.managePlatform}
              >
                <PlatformConfigTabs />
              </PlatformAuthenticatedRoute>
            ),
          },
        ],
      },

      {
        path: "artifacts",
        element: (
          <PlatformAuthenticatedRoute
            action={platformAdminPermissions.viewPlatform}
          >
            <Instruments />
          </PlatformAuthenticatedRoute>
        ),
      },
      {
        path: "email-templates",
        element: (
          <PlatformAuthenticatedRoute
            action={platformAdminPermissions.viewPlatform}
          >
            <EmailTemplates />
          </PlatformAuthenticatedRoute>
        ),
      },
    ],
  },
  // {
  //   path: "surveys/:surveySlug",
  //   element: <SurveyWrapper />,
  // },
  // {
  //   path: "surveys/:surveySlug/thank-you",
  //   element: <ThankYouWrapper />,
  // },
  {
    path: "404",
    element: <Error404 />,
  },
  {
    path: "print/study/:id/phase/:type",
    element: (
      <AuthenticatedRoute>
        <QuestionPrintPhase />
      </AuthenticatedRoute>
    ),
  },
  {
    path: "print/study/:id/form/:formId",
    element: (
      <AuthenticatedRoute>
        <QuestionPrintForm />
      </AuthenticatedRoute>
    ),
  },
  {
    path: "print/response",
    element: <PrintResponse />,
  },
  {
    path: "/studies/:id",
    lazy: async () => {
      const { StudyLayoutComp } = await import("./Routes/StudyRoutes");
      return { Component: StudyLayoutComp };
    },
    children: [
      {
        index: true,
        lazy: async () => {
          const { StudyLandingComp } = await import("./Routes/StudyRoutes");
          return { Component: StudyLandingComp };
        },
      },
      {
        path: "dashboard",
        lazy: async () => {
          const { DashboardComp } = await import("./Routes/StudyRoutes");
          return { Component: DashboardComp };
        },
      },
      {
        path: "dashboard/legacy",
        lazy: async () => {
          const { DashboardComp } = await import("./Routes/StudyRoutes");
          return { Component: DashboardComp };
        },
      },
      {
        path: "dashboard/incomplete-surveys",
        lazy: async () => {
          const { IncompleteSurveysListComp } =
            await import("./Routes/StudyRoutes");
          return { Component: IncompleteSurveysListComp };
        },
      },
      {
        path: "dashboard/timepoint-participants",
        lazy: async () => {
          const { TimepointParticipantsListComp } =
            await import("./Routes/StudyRoutes");
          return { Component: TimepointParticipantsListComp };
        },
      },
      {
        path: "dashboard/survey-delivery-participants",
        lazy: async () => {
          const { SurveyDeliveryParticipantsListComp } =
            await import("./Routes/StudyRoutes");
          return { Component: SurveyDeliveryParticipantsListComp };
        },
      },
      {
        path: "dashboard/stale-sync/:bucket",
        lazy: async () => {
          const { StaleSyncParticipantsListComp } =
            await import("./Routes/StudyRoutes");
          return { Component: StaleSyncParticipantsListComp };
        },
      },
      {
        path: "dashboard/open-survey-signposts",
        lazy: async () => {
          const { OpenSurveySignpostsListComp } =
            await import("./Routes/StudyRoutes");
          return { Component: OpenSurveySignpostsListComp };
        },
      },
      {
        path: "dashboard/follow-up-calls",
        lazy: async () => {
          const { FollowUpCallListComp } =
            await import("./Routes/StudyRoutes");
          return { Component: FollowUpCallListComp };
        },
      },
      {
        path: "study-designer",
        children: [
          {
            index: true,
            lazy: async () => {
              const { StudyDesignerComp } =
                await import("./Routes/StudyRoutes");
              return { Component: StudyDesignerComp };
            },
          },
          {
            path: "form/:formId/edit",
            lazy: async () => {
              const { QuestionBuilderComp } =
                await import("./Routes/StudyRoutes");
              return { Component: QuestionBuilderComp };
            },
          },
          {
            path: "form/:formId/edit-translation",
            lazy: async () => {
              const { QuestionBuilderTranslationComp } =
                await import("./Routes/StudyRoutes");
              return { Component: QuestionBuilderTranslationComp };
            },
          },
          {
            path: "form/:formId/preview",
            lazy: async () => {
              const { QuestionPreviewComp } =
                await import("./Routes/StudyRoutes");
              return { Component: QuestionPreviewComp };
            },
          },
        ],
      },
      {
        path: "dev-tools",
        children: [
          {
            index: true,
            lazy: async () => {
              const { DevToolsComp } = await import("./Routes/StudyRoutes");
              return { Component: DevToolsComp };
            },
          },
          {
            path: "survey-validator",
            lazy: async () => {
              const { SurveyValidatorComp } =
                await import("./Routes/StudyRoutes");
              return { Component: SurveyValidatorComp };
            },
          },
        ],
      },
      {
        path: "participants",
        children: [
          {
            index: true,
            lazy: async () => {
              const { RecordsComp } = await import("./Routes/StudyRoutes");
              return { Component: RecordsComp };
            },
          },
          {
            path: ":participantId",
            children: [
              {
                path: "details",
                lazy: async () => {
                  const { ParticipantDetailsComp } =
                    await import("./Routes/StudyRoutes");
                  return { Component: ParticipantDetailsComp };
                },
              },
              {
                path: "timeline",
                lazy: async () => {
                  const { ParticipantTimelineComp } =
                    await import("./Routes/StudyRoutes");
                  return { Component: ParticipantTimelineComp };
                },
              },
              {
                path: "consent-form/:consentId",
                lazy: async () => {
                  const { ConsentFormComp } =
                    await import("./Routes/StudyRoutes");
                  return { Component: ConsentFormComp };
                },
              },
              {
                path: "responses",
                lazy: async () => {
                  const { ResponsesComp } =
                    await import("./Routes/StudyRoutes");
                  return { Component: ResponsesComp };
                },
              },
              {
                path: "responses/:formId/compare",
                lazy: async () => {
                  const { StudyCompareResponsesWrapperComp } =
                    await import("./Routes/StudyRoutes");
                  return { Component: StudyCompareResponsesWrapperComp };
                },
              },
              // {
              //   path: "analysis",
              //   lazy: async () => {
              //     const { AnalysisDashboardComp } = await import(
              //       "./Routes/StudyRoutes"
              //     );
              //     return { Component: AnalysisDashboardComp };
              //   },
              // },
              {
                path: "prescription",
                lazy: async () => {
                  const { PrescriptionComp } =
                    await import("./Routes/StudyRoutes");
                  return { Component: PrescriptionComp };
                },
              },
            ],
          },
        ],
      },
      {
        path: "participant-groups",
        lazy: async () => {
          const { ParticipantGroupsComp } =
            await import("./Routes/StudyRoutes");
          return { Component: ParticipantGroupsComp };
        },
      },
      {
        path: "e-consent",
        children: [
          {
            index: true,
            lazy: async () => {
              const { EConsentComp } = await import("./Routes/StudyRoutes");
              return { Component: EConsentComp };
            },
          },
          {
            path: "form/:formId/edit",
            lazy: async () => {
              const { ConsentBuilderComp } =
                await import("./Routes/StudyRoutes");
              return { Component: ConsentBuilderComp };
            },
          },
          {
            path: "form/:formId/edit-translation",
            lazy: async () => {
              const { ConsentBuilderComp } =
                await import("./Routes/StudyRoutes");
              return { Component: ConsentBuilderComp };
            },
          },

          {
            path: "form/:formId/preview",
            lazy: async () => {
              const { ConsentPreviewComp } =
                await import("./Routes/StudyRoutes");
              return { Component: ConsentPreviewComp };
            },
          },
          {
            path: "form/:formId/providerSign",
            lazy: async () => {
              const { ConsentPreviewComp } =
                await import("./Routes/StudyRoutes");
              return { Component: ConsentPreviewComp };
            },
          },
        ],
      },
      {
        path: "medication",
        lazy: async () => {
          const { MedicationComp } = await import("./Routes/StudyRoutes");
          return { Component: MedicationComp };
        },
      },
      {
        path: "todo",
        lazy: async () => {
          const { TodoComp } = await import("./Routes/StudyRoutes");
          return { Component: TodoComp };
        },
      },
      {
        path: "information-sheets",
        lazy: async () => {
          const { InformationSheetsComp } = await import(
            "./Routes/StudyRoutes"
          );
          return { Component: InformationSheetsComp };
        },
      },
      {
        path: "audit-trail",
        lazy: async () => {
          const { AuditTrailComp } = await import("./Routes/StudyRoutes");
          return { Component: AuditTrailComp };
        },
      },
      {
        path: "email-delivery-logs",
        lazy: async () => {
          const { EmailDeliveryLogsComp } = await import(
            "./Routes/StudyRoutes"
          );
          return { Component: EmailDeliveryLogsComp };
        },
      },
      {
        path: "export",
        lazy: async () => {
          const { ExportComp } = await import("./Routes/StudyRoutes");
          return { Component: ExportComp };
        },
      },
      {
        path: "econsents-tracking",
        lazy: async () => {
          const { ConsentsTrackingComp } = await import("./Routes/StudyRoutes");
          return { Component: ConsentsTrackingComp };
        },
      },
      {
        path: "medication",
        lazy: async () => {
          const { MedicationComp } = await import("./Routes/StudyRoutes");
          return { Component: MedicationComp };
        },
      },
      {
        path: "study-settings",
        lazy: async () => {
          const { StudySettingsComp } = await import("./Routes/StudyRoutes");
          return { Component: StudySettingsComp };
        },
      },
      {
        path: "login-activity",
        lazy: async () => {
          const { LoginActivityComp } = await import("./Routes/StudyRoutes");
          return { Component: LoginActivityComp };
        },
      },
      // {
      //   path: "study-theme",
      //   lazy: async () => {
      //     const { StudyThemePanelComp } = await import("./Routes/StudyRoutes");
      //     return { Component: StudyThemePanelComp };
      //   },
      // },
      {
        path: "users",
        children: [
          {
            index: true,
            lazy: async () => {
              const { UsersComp } = await import("./Routes/StudyRoutes");
              return { Component: UsersComp };
            },
          },
          {
            path: ":userId",
            lazy: async () => {
              const { UserDetailComp } = await import("./Routes/StudyRoutes");
              return { Component: UserDetailComp };
            },
          },
        ],
      },

      {
        path: "form-permissions",
        lazy: async () => {
          const { FormPermissionsComp } = await import("./Routes/StudyRoutes");
          return { Component: FormPermissionsComp };
        },
      },
      {
        path: "permissions",
        lazy: async () => {
          const { RolesListComp } = await import("./Routes/StudyRoutes");
          return { Component: RolesListComp };
        },
      },
      {
        path: "queries",
        lazy: async () => {
          const { QueriesComp } = await import("./Routes/StudyRoutes");
          return { Component: QueriesComp };
        },
      },
      {
        path: "appointments",
        children: [
          {
            index: true,
            lazy: async () => {
              const { AppointmentsComp } = await import("./Routes/StudyRoutes");
              return { Component: AppointmentsComp };
            },
          },
          {
            path: "list",
            lazy: async () => {
              const { AppointmentListViewComp } =
                await import("./Routes/StudyRoutes");
              return { Component: AppointmentListViewComp };
            },
          },
        ],
      },
      {
        path: "my-availability",
        lazy: async () => {
          const { MyAvailabilityComp } = await import("./Routes/StudyRoutes");
          return { Component: MyAvailabilityComp };
        },
      },

      {
        path: "messages",
        lazy: async () => {
          const { MessagesComp } = await import("./Routes/StudyRoutes");
          return { Component: MessagesComp };
        },
      },
      {
        path: "exercise",
        children: [
          {
            index: true,
            lazy: async () => {
              const { ExerciseCollectionComp } =
                await import("./Routes/StudyRoutes");
              return { Component: ExerciseCollectionComp };
            },
          },
          {
            path: ":exerciseId/categories",
            lazy: async () => {
              const { ExerciseCollectionDetailsComp } =
                await import("./Routes/StudyRoutes");
              return { Component: ExerciseCollectionDetailsComp };
            },
          },
          {
            path: ":exerciseId/categories/:categoryId/sections",
            lazy: async () => {
              const { SectionBuilderComp } =
                await import("./Routes/StudyRoutes");
              return { Component: SectionBuilderComp };
            },
          },
          {
            path: "builder",
            lazy: async () => {
              const { ExerciseBuilderComp } =
                await import("./Routes/StudyRoutes");
              return { Component: ExerciseBuilderComp };
            },
          },
        ],
      },
      {
        path: "cms",
        children: [
          {
            index: true,
            lazy: async () => {
              const { CMSComp } = await import("./Routes/StudyRoutes");
              return { Component: CMSComp };
            },
          },
          {
            path: "learning/:categoryId",
            lazy: async () => {
              const { LearningModuleDetailsComp } =
                await import("./Routes/StudyRoutes");
              return { Component: LearningModuleDetailsComp };
            },
          },
          {
            path: ":moduleId",
            lazy: async () => {
              const { UnitsComp } = await import("./Routes/StudyRoutes");
              return { Component: UnitsComp };
            },
          },
          {
            path: "units/:moduleId",
            lazy: async () => {
              const { PagesComp } = await import("./Routes/StudyRoutes");
              return { Component: PagesComp };
            },
          },
          {
            path: "review/:moduleId",
            lazy: async () => {
              const { ReviewsComp } = await import("./Routes/StudyRoutes");
              return { Component: ReviewsComp };
            },
          },
          {
            path: "builder/:unitId",
            lazy: async () => {
              const { CMSBuilderComp } = await import("./Routes/StudyRoutes");
              return { Component: CMSBuilderComp };
            },
          },
          {
            path: "preview/:unitId",
            lazy: async () => {
              const { CMSPreviewWrapperComp } =
                await import("./Routes/StudyRoutes");
              return { Component: CMSPreviewWrapperComp };
            },
          },
        ],
      },
    ],
  },
  {
    path: "study/:studySlug",
    children: [
      {
        index: true,
        element: <LandingPage />,
      },
      {
        path: "check-email",
        element: <CheckEmail />,
      },
    ],
  },
  {
    path: "/privacy-policy",
    element: <PrivacyPolicy />,
  },
  {
    path: "participant/screening/:id",
    element: (
      <AuthenticatedRoute allowedRoles={["participant"]}>
        <CheckFlags />
      </AuthenticatedRoute>
    ),
  },
  {
    path: "participant/thank-you",
    element: <ThankYou />,
  },
  //get study
  // {
  //   path: "/participant",
  //   lazy: async () => {
  //     const { ParticipantLayoutComp } =
  //       await import("./Routes/ParticipantsRoutes");
  //     return { Component: ParticipantLayoutComp };
  //   },
  //   children: [
  //     {
  //       path: "home",
  //       lazy: async () => {
  //         const { HomeComp } = await import("./Routes/ParticipantsRoutes");
  //         return { Component: HomeComp };
  //       },
  //     },
  //     {
  //       path: "questionnaires",
  //       lazy: async () => {
  //         const { QuestionnaireComp } =
  //           await import("./Routes/ParticipantsRoutes");
  //         return { Component: QuestionnaireComp };
  //       },
  //     },
  //     {
  //       path: "questionnaires/:id/survey/:surveyId",
  //       lazy: async () => {
  //         const { SurveyComp } = await import("./Routes/ParticipantsRoutes");
  //         return { Component: SurveyComp };
  //       },
  //     },
  //     {
  //       path: "e-consent",
  //       children: [
  //         {
  //           index: true,
  //           lazy: async () => {
  //             const { EConsentComp } =
  //               await import("./Routes/ParticipantsRoutes");
  //             return { Component: EConsentComp };
  //           },
  //         },
  //       ],
  //     },
  //     {
  //       path: "appointments",
  //       lazy: async () => {
  //         const { ParticipantAppointmentsComp } =
  //           await import("./Routes/ParticipantsRoutes");
  //         return { Component: ParticipantAppointmentsComp };
  //       },
  //     },
  //   ],
  // },
  {
    path: "sign/:id",
    lazy: async () => {
      const { SignConsent } = await import("./Routes/ParticipantsRoutes");
      return { Component: SignConsent };
    },
  },
  {
    path: "mobile/*",
    element: <MobileAppLanding />,
  },
  {
    path: "link/participant/redirect/:type",
    element: <LegacyParticipantLinkRedirect />,
  },
  {
    path: "/telehealth/:studyId/:apptId",
    lazy: async () => {
      const { OpenTokComp } = await import("./Routes/StudyRoutes");
      return { Component: OpenTokComp };
    },
  },

  {
    path: "/check-email",
    element: <WebviewWrapper />,
  },
  {
    path: "/lesson/read/:unitId",
    element: <WebviewWrapper />,
  },
  {
    path: "/",
    element: <Navigate to="/home" replace />,
  },
  {
    path: "*",
    element: <Navigate to="/home" replace />,
  },
]);

export default routes;
